import {
  closeCurrentDialog,
  FormSubmitHandler,
  openDialog,
  showNotification,
  useLocalStorage,
} from 'platform/components';

import {useCallback, useEffect, useState} from 'react';

import {equals, isNotNil} from 'ramda';

import {
  SourcingPresetResponseBody,
  useLazySourcingGetPresetQuery,
  useSourcingCreatePresetMutation,
  useSourcingGetPresetsListQuery,
  useSourcingUpdatePresetMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, useOnMount, useQueryState} from 'shared/web';

import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {
  FilterPresetForm,
  SourcingFilterPresetFormData,
} from '../components/FilterPresetForm/FilterPresetForm';
import {useFilters} from '../components/FiltersContext/FiltersContext';
import {DEFAULT_FILTERS, FiltersType} from '../types/FiltersType';
import {convertFiltersBodyToFiltersType} from '../utils/convertFiltersBodyToFiltersType';
import {convertFiltersTypeToFiltersBody} from '../utils/convertFiltersTypeToFiltersBody';

type SourcingFilterPreset = Omit<SourcingPresetResponseBody, 'filters'> & {
  filters: FiltersType;
};

export type SourcingFilterPresetsControl = {
  selectedFilterPresetId: string | Nullish;
  onSelectFilterPresetId: (id: string) => void;
  onCreateFilterPreset: () => void;
  onUpdateSelectedFilterPreset: () => void;
  onResetFilterPreset: () => void;
  selectedFilterPreset: SourcingFilterPreset | Nullish;
  isOutdated: boolean;
  isEmpty: boolean;
};

const useInitialFilterPresetId = () =>
  useLocalStorage<string | null>('dms--sourcing-filter-preset-id', null);

export function useSourcingFilterPresets(filterModalOpened: boolean): SourcingFilterPresetsControl {
  const {filters, onChange} = useFilters();
  const targetCountry = useSourcingTargetCountry();
  const currency = useSourcingCurrency();
  const sourcingType = useSourcingType();
  const [queryFilterPresetId, , clearQueryFilterPresetId] = useQueryState('filterPresetId');
  // selectedFilterPresetId is for fast interaction after click on button
  const [selectedFilterPresetId, setSelectedFilterPresetId] = useState<string | Nullish>();

  // selectedFilterPreset is filter preset loaded from api after click on button
  const [selectedFilterPreset, setSelectedFilterPreset] = useState<
    SourcingFilterPreset | Nullish
  >();

  const [savedFilterPresetId, saveFilterPresetId] = useInitialFilterPresetId();

  const {data: presetsList} = useSourcingGetPresetsListQuery({
    country: targetCountry?.code,
    currency: currency?.code,
  });

  const [loadPreset] = useLazySourcingGetPresetQuery();

  const resetFilterPreset = useCallback(() => {
    setSelectedFilterPresetId(null);
    setSelectedFilterPreset(null);
    saveFilterPresetId(null);
  }, [saveFilterPresetId]);

  const loadFilterPreset = useCallback(
    (presetId: string, options?: {saveToLocalStorage?: boolean; clearQuery?: boolean}) => {
      setSelectedFilterPresetId(presetId);
      loadPreset({id: presetId})
        .unwrap()
        .then((filterPreset) => {
          setSelectedFilterPreset({
            ...filterPreset,
            filters: convertFiltersBodyToFiltersType(filterPreset.filters),
          });
          onChange(convertFiltersBodyToFiltersType(filterPreset.filters));
          if (options?.saveToLocalStorage) {
            saveFilterPresetId(filterPreset.id);
          }
        })
        .catch(() => {
          setSelectedFilterPresetId(null);
          setSelectedFilterPreset(null);
          if (options?.saveToLocalStorage) {
            saveFilterPresetId(null);
          }
        })
        .finally(() => {
          if (options?.clearQuery) {
            clearQueryFilterPresetId();
          }
        });
    },
    [loadPreset, onChange, saveFilterPresetId, clearQueryFilterPresetId]
  );

  // Load filter preset from local storage on mount (only if no query parameter)
  useOnMount(() => {
    if (!queryFilterPresetId && savedFilterPresetId) {
      loadFilterPreset(savedFilterPresetId, {saveToLocalStorage: false});
    }
  });

  // Load filter preset when query parameter changes (or on mount if query parameter exists)
  useEffect(() => {
    if (queryFilterPresetId) {
      loadFilterPreset(queryFilterPresetId, {saveToLocalStorage: true, clearQuery: true});
    }
  }, [queryFilterPresetId, loadFilterPreset]);

  const isOutdated =
    isNotNil(selectedFilterPreset) &&
    !equals(filters, {
      ...DEFAULT_FILTERS,
      ...selectedFilterPreset?.filters,
    });

  /* Unselect filter preset when:
   - selected filters !== filters from filter preset
   - and (filters change when filter modal is closed
          - or on filter modal close) */
  useEffect(() => {
    if (!filterModalOpened && isOutdated) {
      resetFilterPreset();
    }
  }, [filterModalOpened, isOutdated, resetFilterPreset]);

  const [createPreset] = useSourcingCreatePresetMutation();
  const [updatePreset] = useSourcingUpdatePresetMutation();

  const onSelectFilterPresetId = (filterPresetId: string) => {
    if (filterPresetId === selectedFilterPresetId) {
      resetFilterPreset();
      return;
    }

    loadFilterPreset(filterPresetId, {saveToLocalStorage: true});
  };

  const onCreateFilterPreset = () => {
    const onSubmit: FormSubmitHandler<SourcingFilterPresetFormData> = (values) =>
      createPreset({
        createPresetRequestBody: {
          name: values.name,
          allowNotifications: values.allowNotifications ?? false,
          filters: convertFiltersTypeToFiltersBody(filters),
        },
      })
        .unwrap()
        .then((createdFilterPreset) => {
          closeCurrentDialog();
          onSelectFilterPresetId(createdFilterPreset.id);
        })
        .catch(handleApiError);

    openDialog(
      <FilterPresetForm
        initialValues={{
          name: selectedFilterPreset?.name ?? '',
          allowNotifications: sourcingType === SourcingType.AUCTIONS ? false : true,
        }}
        onSubmit={onSubmit}
      />,
      {title: i18n.t('entity.sourcing.filterPresets.labels.create')}
    );
  };

  const onUpdateSelectedFilterPreset = () => {
    if (!selectedFilterPresetId) {
      return;
    }

    return updatePreset({
      id: selectedFilterPresetId,
      updatePresetRequestBody: {
        filters: convertFiltersTypeToFiltersBody(filters),
      },
    })
      .unwrap()
      .then(() => {
        setSelectedFilterPreset((prev) => (isNotNil(prev) ? {...prev, filters} : prev));
        closeCurrentDialog();
        showNotification.success(i18n.t('general.notifications.success'));
      })
      .catch(handleApiError);
  };

  return {
    selectedFilterPresetId,
    onSelectFilterPresetId,
    onCreateFilterPreset,
    onUpdateSelectedFilterPreset,
    onResetFilterPreset: resetFilterPreset,
    selectedFilterPreset,
    isOutdated,
    isEmpty: (presetsList?.length ?? 0) === 0,
  };
}
